import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Button, classnames, Icon, Loader, ModalDialog } from "@culturehq/components";
import { makePost } from "@culturehq/client";

import { Organization, TPromptQuestion, StoryQuestion, Suggestion, SuggestionContent } from "chq";
import { useIsLoggedIn, useIsMobile } from "app/hooks";
import { t, Trans } from "app/utils";

import ChqModalDialog from "components/common/ChqModalDialog";
import CropImage from "components/stories/post-stories-v2/common/PostStoryForm/CropImage";
import { PostStoryProvider, usePostStoryContext } from "components/stories/post-stories-v2/contexts/PostStoryContext";
import ProgressLine from "components/admin/analytics/ProgressLine";
import ErrorMessage from "components/common/ErrorMessage";
import useEditorSerializer from "app/hooks/useEditorSerializer";
import ChqModalConfirm from "components/common/ChqModalConfirm";
import UserInformationForm from "components/stories/post-stories-v2/common/UserInformation/UserInformationForm";
import IntroPage from "../intro-page/IntroPage";
import PromptEdit from "../prompt-question/PromptEdit";
import PutTogetherStory from "../prompt-question/PutTogetherStory";
import PromptAddMedia from "../prompt-question/PromptAddMedia";
import PostStoryModalButton from "./PostStoryModalButton";
import FinalizePage from "../finalize-page/FinalizePage";
import ProgressBar from "./ProgressBar";
import MobileHeader from "./MobileHeader";
import PromptQuestion from "../prompt-question/PromptQuestion";

const DEFAULT_STEPS = 4;
const MIN_ANSWER_CHARS = 5;

type PostStoryModalProps = {
  preFilledQuestion: StoryQuestion;
  organization: Organization;
  buttonText?: React.ReactNode;
};

const BackButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button
    className="MainContainerIntelligence__Navigation-btn-prev"
    inverted
    onClick={onClick}
  >
    <Trans>Back</Trans>
  </Button>
);

const PostStoryModal: React.FC<PostStoryModalProps> = ({
  preFilledQuestion: prompt,
  buttonText
}) => {
  const isLoggedIn = useIsLoggedIn();
  const isMobile = useIsMobile();
  const {
    organization,
    aiStoryBodyRef,
    state: {
      selectedImages,
      selectedMedia,
      selectedPrompt,
      progress
    },
    open,
    onOpen,
    onConfirmClose,
    onConfirmCloseAi,
    setState,
    handleSubmit
  } = usePostStoryContext();
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [stepsProgress, setStepsProgress] = useState(0);
  const [questions, setQuestions] = useState<TPromptQuestion[]>(
    prompt.promptQuestions as TPromptQuestion[]
  );
  const [suggestion, setSuggestion] = useState<Suggestion>();
  const [chatId, setChatId] = useState<string>();
  const [isLoading, setLoading] = useState(false);

  const [isChanged, setIsChanged] = useState(false);
  const textStory = suggestion?.story as Suggestion["story"];
  const answers = useMemo(() => questions.map(q => q.answer), [questions]);

  const checkPromptAnswerPostingStep = (step: number) => step === (numberOfQuestions + 1);
  const numberOfQuestions = (prompt.promptQuestions?.length || 0);
  const numberOfSteps = DEFAULT_STEPS + numberOfQuestions + (isLoggedIn ? 0 : 1);
  const isConfirmPostStep = numberOfSteps + 1;
  const lastStep = numberOfSteps;
  const isPromptAnswerPostingStep = checkPromptAnswerPostingStep(step);
  const isStorySuggestionStep = step === (numberOfQuestions + 2) && chatId;
  const isMediaSuggestionStep = step === (numberOfQuestions + 3);
  const isSendingStoryStep = step === isConfirmPostStep && (prompt.visibility === "private_access" || isLoggedIn);
  const isFinalizeStoryStep = step === (isLoggedIn ? numberOfSteps : numberOfSteps - 1)
    || isSendingStoryStep;
  const isPublicAccessStep = prompt.visibility === "public_access" && !isLoggedIn && step === lastStep;
  const showIntroPage = step === 0;
  const showPromptQuestion = step > 0 && step <= numberOfQuestions && questions;
  const showSuggestions = step > numberOfQuestions && suggestion;
  // eslint-disable-next-line max-len
  const showStepButtons = step !== 0 && !isPromptAnswerPostingStep && !isPublicAccessStep && !isSendingStoryStep;

  const isSubmitting = progress !== 0;
  const nextButtonLabel = step < numberOfSteps ? "Next" : "Post Story";
  const buttonLabel = isSubmitting ? "Submitting" : nextButtonLabel;
  const isAnswerTooShort = showPromptQuestion
    && (questions[step - 1].answer?.length || 0) < MIN_ANSWER_CHARS;
  const isMediaSuggestionValid = selectedImages.length > 0
    || (!!selectedMedia && !(selectedMedia as any).thumbUrl);
  const disableNextButton = isSubmitting || isAnswerTooShort
    || (isMediaSuggestionStep && !isMediaSuggestionValid);

  const mobileHeaderText = useMemo(
    () => {
      if (showPromptQuestion) return "Answer Question";
      if (isStorySuggestionStep) return "Edit Story";
      if (isMediaSuggestionStep) return "Add Photo or Video";
      if (isFinalizeStoryStep) return "Finalize Story";
      if (isPublicAccessStep) return "Add your Information";
      return "";
    }, [isFinalizeStoryStep, isMediaSuggestionStep, isPublicAccessStep,
      isStorySuggestionStep, showPromptQuestion]
  );

  useEditorSerializer(textStory, aiStoryBodyRef);

  useEffect(() => {
    const progressValue = (step / numberOfSteps) * 100;
    setStepsProgress(progressValue);
  }, [step, numberOfSteps]);

  const restartState = () => {
    setStep(0);
    setQuestions(
      (prompt.promptQuestions as TPromptQuestion[])
        .map(q => ({ ...q, answer: undefined })));
    setSuggestion(undefined);
    setChatId("");
  };

  useEffect(() => {
    if (open || step === 0) return;

    restartState();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(
    () => {
      setState({ questions: questions.map(q => ({ id: q.id, answer: q.answer })) });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions]
  );

  useEffect(
    () => {
      let suggestions: SuggestionContent[] = suggestion?.media?.images || [];

      if (suggestion?.media?.video) {
        suggestions = suggestions.concat([{
          title: "Video suggestion",
          content: suggestion?.media?.video
        }]);
      }

      setState({ suggestions });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [suggestion]
  );

  useEffect(() => {
    if (!isSendingStoryStep) return;

    handleSubmit({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSendingStoryStep]);

  useEffect(() => {
    if (!isPromptAnswerPostingStep) return;

    makePost("/ai/public/story_suggestion", {
      story_question_id: prompt.id,
      answers: JSON.stringify(questions.map(({ question, answer }) => ({ question, answer })))
    })
      .then(({ suggestion, chatId }: any) => {
        setSuggestion(suggestion);
        setChatId(chatId);
        handleNextStep();
      })
      .catch(_error => {
        handlePrevStep();
        setError(t("Something went wrong, please try again"));
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt.id, isPromptAnswerPostingStep, questions]);

  useEffect(() => {
    if (suggestion?.story || (answers && answers.some(answer => answer))) {
      setIsChanged(true);
    } else {
      setIsChanged(false);
    }
  }, [answers, suggestion]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isChanged) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = t("You have unsaved changes, are you sure you'd like to close the prompt modal?");
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isChanged]);

  const handleNextStep = () => {
    if (error) setError("");
    if (showPromptQuestion && (questions[step - 1].answer?.length || 0) < MIN_ANSWER_CHARS) {
      return;
    }
    if (isMediaSuggestionStep && !isMediaSuggestionValid && !isMobile) return;

    setStep(prevStep => Math.min(prevStep + 1, isConfirmPostStep));
  };

  const handlePrevStep = () => {
    if (error) setError("");
    if (isStorySuggestionStep) {
      setStep(prevStep => prevStep - 2);
      return;
    }
    setStep(prevStep => Math.max(prevStep - 1, 0));
  };

  const handleOnChangeAnswer = ({ id, answer }: Partial<TPromptQuestion>) => {
    setQuestions(prev => {
      const questions = [...prev];
      const questionIdx = questions.findIndex(a => a.id === id);
      questions[questionIdx].answer = answer;
      return questions;
    });
  };

  const handleSuggestionChange = (suggestion: Partial<Suggestion>) => {
    setSuggestion(prev => ({
      ...prev as Suggestion,
      ...suggestion,
      media: {
        ...prev?.media,
        ...suggestion.media
      }
    }));
  };

  const handleClose = (event?: React.MouseEvent) => {
    if (event?.defaultPrevented) return;

    if (isChanged) {
      // eslint-disable-next-line no-alert
      const confirmExit = window.confirm(
        t("You have unsaved changes, are you sure you'd like to close the prompt modal")
      );
      if (!confirmExit) {
        // eslint-disable-next-line no-unused-expressions
        event?.preventDefault();
        return;
      }
    }

    onConfirmClose();
  };

  return (
    <>
      {open && (
        <ChqModalDialog
          entrance="zoomIn"
          className={classnames(organization?.name, "post-story-modal-ainteligence", { disableOverflowY: isSubmitting })}
          ariaLabelledBy="ai-story"
          ariaDescribedby="ai-story"
        >
          <ModalDialog.Heading
            bigCloseIcon
            onClose={handleClose}
            className={isMobile && !showIntroPage ? "hidden-xs" : ""}
            titleId="ai-story"
          >
            <Fragment />
          </ModalDialog.Heading>
          <ModalDialog.Body className="no-scrollbar">
            {isLoading && <Loader loading />}
            {isMobile && !showIntroPage && !isPromptAnswerPostingStep && (
              <MobileHeader
                isSubmitting={isSubmitting}
                onClose={handleClose}
                onPrevStep={handlePrevStep}
                title={mobileHeaderText}
              />
            )}
            {!isPromptAnswerPostingStep && stepsProgress !== 0 && (
              <ProgressBar progress={stepsProgress} />
            )}
            {(!isMobile || showIntroPage) && <div className="organization-logo" style={{ backgroundImage: `url(${organization?.logo?.originalUrl})` }} />}
            {error && <ErrorMessage error={error} />}
            <div className="MainContainerIntelligence">
              {showIntroPage && (
                <IntroPage onNextStep={handleNextStep} prompt={prompt} />
              )}
              {showPromptQuestion && (
                <PromptQuestion
                  index={step}
                  question={questions[step - 1]}
                  onChange={handleOnChangeAnswer}
                  onNextStep={handleNextStep}
                />
              )}
              {isPromptAnswerPostingStep && (<PutTogetherStory />)}
              {showSuggestions && (
                <>
                  {isStorySuggestionStep && (
                    <PromptEdit
                      chatId={chatId as string}
                      storyQuestionId={prompt.id}
                      textStory={textStory}
                      onSuggestionChange={handleSuggestionChange}
                      onLoading={setLoading}
                    />
                  )}
                  {isMediaSuggestionStep && (
                    <PromptAddMedia
                      prompt={prompt}
                      suggestion={suggestion as Suggestion}
                      chatId={chatId as string}
                      storyQuestionId={prompt.id}
                      onSuggestionChange={handleSuggestionChange}
                      onLoading={setLoading}
                      onNextStep={handleNextStep}
                    />
                  )}
                  {isFinalizeStoryStep && (
                    <FinalizePage
                      onSuggestionChange={handleSuggestionChange}
                      textStory={textStory}
                    />
                  )}
                  {isPublicAccessStep && (
                    <div className="post-story-modal">
                      <UserInformationForm />
                    </div>
                  )}
                </>
              )}
              {showStepButtons && !isMobile && (
                <div className="MainContainerIntelligence__Navigation">
                  {!isSubmitting && (
                    <>
                      {step === (numberOfQuestions + 2) ? (
                        <ChqModalConfirm
                          trigger={(onTrigger: any) => (
                            <BackButton onClick={onTrigger} />
                          )}
                          accept={t("Ok")}
                          onAccept={handlePrevStep}
                        >
                          <Trans>
                            Are you sure you want to go back?
                            You may lose your current story.
                          </Trans>
                        </ChqModalConfirm>
                      ) : (
                        <BackButton onClick={handlePrevStep} />
                      )}
                    </>
                  )}
                  {step === (numberOfQuestions + 2) && (
                    <span className="MainContainerIntelligence__Navigation-between-text">
                      <Trans>Next: Let&apos;s add some media!</Trans>
                    </span>
                  )}
                  {step === (numberOfQuestions + 3) && (
                    <span className="MainContainerIntelligence__Navigation-between-text">
                      <Trans>Next: Preview and submit story!</Trans>
                    </span>
                  )}
                  <Button
                    className="MainContainerIntelligence__Navigation-btn-next"
                    disabled={disableNextButton}
                    onClick={handleNextStep}
                  >
                    <Trans>{buttonLabel}</Trans>
                  </Button>
                </div>
              )}
            </div>
            {isMobile && !isMediaSuggestionStep && (
              <div className="MobileButtons col-xs-12">
                {showIntroPage && (
                  <Button className="NextBtn" onClick={handleNextStep}>
                    <Trans>Let&apos;s get started</Trans>
                    <Icon className="arrow-icon" icon="arrow-left-c" />
                  </Button>
                )}
                {showStepButtons && (
                  <Button
                    className="NextBtn"
                    disabled={disableNextButton}
                    onClick={handleNextStep}
                  >
                    <Trans>{buttonLabel}</Trans>
                  </Button>
                )}
              </div>
            )}
            <div className="MainContainerIntelligence__ProgressBar">
              {isSubmitting && (
                <ProgressLine
                  className="story-progressbar"
                  color="#79b17d"
                  valueNow={progress}
                  valueMax={100}
                />
              )}
            </div>
            <CropImage />
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
      <PostStoryModalButton points={prompt.points} onOpen={onOpen} buttonText={buttonText} />
    </>
  );
};

export default (props: PostStoryModalProps) => (
  <PostStoryProvider {...props}>
    <PostStoryModal {...props} />
  </PostStoryProvider>
);
